/* tanstack table */
import { Table, TableState } from '@tanstack/react-table';

/* React */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

interface AugmentedPartialTableState extends Partial<TableState> {
    tableView: string;
}

/**
 * A custom hook that synchronizes the table state with URL parameters.
 *
 * @template TData The type of data in the table.
 * @param {Object} props The hook properties
 * @param {boolean} [props.ignoreSearchParams] - A flag to ignore URL parameters.
 * @param {Table<TData>} props.table - The table instance from @tanstack/react-table.
 * @param {string} props.tableName - A unique identifier for the table, used as a prefix for URL parameters.
 * @param {string} props.tableViewID - The ID of the current table view to be stored in URL.
 * @returns {void}
 */
export const usePutTableStateInURL = <TData>({
    ignoreSearchParams,
    table,
    tableName,
    tableViewID
}: {
    ignoreSearchParams?: boolean;
    table: Table<TData>;
    tableName: string;
    tableViewID: string;
}): void => {
    const [, setSearchParams] = useSearchParams();

    // Extract relevant parts of the table state
    const { columnFilters, columnOrder, columnVisibility, sorting } =
        table.getState();

    // Sync URL with table state on change
    useEffect(() => {
        if (tableName === undefined || ignoreSearchParams || !tableViewID) {
            return;
        }

        // Only sync if a table name is provided
        setSearchParams(
            (searchParams) => {
                // Prepare an object with the table state we want to synchronize
                const tableState: AugmentedPartialTableState = {
                    columnFilters,
                    columnOrder,
                    columnVisibility,
                    sorting,
                    tableView: tableViewID
                };
                // Update URL parameters with the current table state
                Object.entries(tableState).forEach(([key, value]) => {
                    searchParams.set(
                        `${tableName}.${key}`,
                        JSON.stringify(value)
                    );
                });

                return searchParams;
            },
            // replace is true because of how we update the URL on initial page load.
            // When there are no params in the URL, we add a default set of table params to the URL, which adds a new entry to the browser’s navigation history.
            // If we were to not replace that entry, when you click back, the browser would just navigate back to the entry with no url params,
            // and the default set would be added to the URL again.
            { replace: true }
        );
    }, [
        columnFilters,
        columnOrder,
        columnVisibility,
        ignoreSearchParams,
        sorting,
        tableName,
        setSearchParams,
        tableViewID
    ]);
};
